import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { gameWsUrl } from "@/lib/ws";
import { quizImageUrl } from "@/lib/quizImage";
import { ensureAudio, playReveal, playRanking, playTick, playGo } from "@/lib/sfx";
import confetti from "canvas-confetti";
import Logo from "@/components/Logo";
import { GamePinBar } from "@/components/GamePinBar";
import { Button } from "@/components/ui/button";
import { Users, Play, ChevronRight, Trophy, X, Clock, BarChart3, Loader2, Crown, Medal, CheckCircle2 } from "lucide-react";

const colors = ["bg-brand-blue", "bg-brand-purple", "bg-brand-green", "bg-brand-yellow"];

const podium = [
  { wrap: "bg-gradient-to-br from-amber-300 to-amber-500 text-brand-ink ring-2 ring-amber-200", badge: "bg-brand-ink text-amber-300", Icon: Crown },
  { wrap: "bg-gradient-to-br from-slate-100 to-slate-300 text-brand-ink ring-2 ring-white/60", badge: "bg-brand-ink text-slate-200", Icon: Medal },
  { wrap: "bg-gradient-to-br from-orange-300 to-orange-600 text-white ring-2 ring-orange-200", badge: "bg-brand-ink text-orange-200", Icon: Medal },
];

const BGS = [
  { id: "classroom-bg.jpg", label: "Sala de aula" },
  { id: "classroom-bg-science.jpg", label: "Laboratório" },
  { id: "classroom-bg-library.jpg", label: "Biblioteca" },
];

export default function Apresentar() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const revealTimerRef = useRef(null);
  const [info] = useState(() =>
    location.state || JSON.parse(sessionStorage.getItem(`educarte_host_${id}`) || "null")
  );
  const [phase, setPhase] = useState("lobby");
  const [players, setPlayers] = useState([]);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [question, setQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answered, setAnswered] = useState({ answered: 0, total: 0 });
  const [reveal, setReveal] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [ended, setEnded] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [timeFrac, setTimeFrac] = useState(1);
  const [bg, setBg] = useState("classroom-bg.jpg");
  const [autoStartIn, setAutoStartIn] = useState(null);
  const [revealCountdown, setRevealCountdown] = useState(null);

  const send = useCallback((msg) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(msg));
  }, []);

  // 3-2-1 countdown before the quiz starts, then send start to the server.
  const startCountdown = useCallback(() => {
    ensureAudio();
    setPhase("countdown");
    setCountdown(3);
    playTick();
    let n = 3;
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(iv);
        playGo();
        setCountdown("GO");
        setTimeout(() => send({ action: "start", auto_advance: autoAdvance }), 550);
      } else {
        setCountdown(n);
        playTick();
      }
    }, 1000);
  }, [autoAdvance, send]);

  useEffect(() => {
    if (!info) {
      navigate("/quizzes");
      return;
    }
    sessionStorage.setItem(`educarte_host_${id}`, JSON.stringify(info));
    const ws = new WebSocket(gameWsUrl(id, { role: "host", token: info.host_token }));
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "lobby") setPlayers(m.players);
      else if (m.type === "question") {
        setQuestion(m);
        setReveal(null);
        setRanking(null);
        setAnswered({ answered: 0, total: players.length });
        setPhase("question");
      } else if (m.type === "answered") setAnswered({ answered: m.answered, total: m.total });
      else if (m.type === "reveal_countdown") {
        ensureAudio();
        setPhase("revealing");
        let n = m.seconds || 2;
        setRevealCountdown(n);
        playTick();
        if (revealTimerRef.current) clearInterval(revealTimerRef.current);
        revealTimerRef.current = setInterval(() => {
          n -= 1;
          if (n <= 0) {
            clearInterval(revealTimerRef.current);
            revealTimerRef.current = null;
          } else {
            setRevealCountdown(n);
            playTick();
          }
        }, 1000);
      } else if (m.type === "reveal") {
        if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
        setReveal(m);
        setPhase("reveal");
        playReveal();
      } else if (m.type === "ranking") {
        setRanking(m);
        setPhase("ranking");
        playRanking();
      } else if (m.type === "ended") {
        setEnded(m);
        setPhase("ended");
      }
    };
    const ping = setInterval(() => {
      if (ws.readyState === 1) ws.send(JSON.stringify({ action: "ping" }));
    }, 25000);
    return () => {
      clearInterval(ping);
      if (revealTimerRef.current) clearInterval(revealTimerRef.current);
      ws.close();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (phase !== "question" || !question?.ends_at) return;
    const tick = () => {
      const remain = question.ends_at - Date.now() / 1000;
      setTimeLeft(Math.max(0, Math.round(remain)));
      setTimeFrac(Math.max(0, Math.min(1, question.time_limit ? remain / question.time_limit : 0)));
    };
    tick();
    const idt = setInterval(tick, 200);
    return () => clearInterval(idt);
  }, [phase, question]);

  // Confetti celebration on the final podium (timed to the 1st-place reveal)
  useEffect(() => {
    if (phase !== "ended") return;
    const burst = setTimeout(() => {
      confetti({ particleCount: 150, spread: 100, startVelocity: 48, ticks: 240, origin: { y: 0.35 }, zIndex: 60 });
      confetti({ particleCount: 70, angle: 60, spread: 70, origin: { x: 0, y: 0.65 }, zIndex: 60 });
      confetti({ particleCount: 70, angle: 120, spread: 70, origin: { x: 1, y: 0.65 }, zIndex: 60 });
    }, 700);
    const end = Date.now() + 2800;
    const iv = setInterval(() => {
      if (Date.now() > end) { clearInterval(iv); return; }
      confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0 }, zIndex: 60 });
      confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1 }, zIndex: 60 });
    }, 260);
    return () => { clearTimeout(burst); clearInterval(iv); };
  }, [phase]);

  // Auto-start: in automatic mode, start 15s after a player joins; reset on each new join.
  useEffect(() => {
    if (phase !== "lobby" || !autoAdvance || players.length === 0) {
      setAutoStartIn(null);
      return;
    }
    let n = 15;
    setAutoStartIn(15);
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(iv);
        setAutoStartIn(null);
        startCountdown();
      } else {
        setAutoStartIn(n);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, autoAdvance, players.length, startCountdown]);

  if (!info) return null;
  const joinUrl = `${window.location.origin}/jogar?pin=${info.pin}`;
  const classroomBg = {
    backgroundImage:
      `linear-gradient(rgba(30,27,75,0.74), rgba(76,29,149,0.80)), url('/${bg}')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  // LOBBY
  if (phase === "lobby") {
    return (
      <div className="min-h-screen text-white flex flex-col" style={classroomBg}>
        <div className="flex items-center justify-between px-6 h-16"><Logo light /><span className="text-white/70 text-sm">{players.length} jogadores</span></div>
        <div className="flex-1 grid lg:grid-cols-2 gap-8 items-center p-6 max-w-6xl mx-auto w-full">
          <div className="text-center lg:text-left">
            <p className="uppercase tracking-widest text-white/70 text-sm font-bold">Entre em {window.location.host}/jogar</p>
            <p className="mt-3 text-white/80">Token do jogo</p>
            <div className="font-heading text-7xl sm:text-8xl font-black tracking-widest" data-testid="game-pin">{info.pin}</div>
            <div className="mt-6 inline-block bg-white p-4 rounded-2xl">
              <QRCodeSVG value={joinUrl} size={160} />
            </div>
          </div>
          <div className="bg-white/10 rounded-3xl p-6 backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold flex items-center gap-2"><Users className="w-5 h-5" /> Sala de espera</h2>
              <div className="flex flex-col items-end">
                <Button onClick={startCountdown} disabled={!players.length} className="rounded-full bg-brand-green hover:bg-emerald-600 gap-2" data-testid="start-game-btn">
                  <Play className="w-4 h-4" /> Iniciar
                </Button>
                {autoAdvance && autoStartIn != null && (
                  <span className="mt-1.5 text-xs text-brand-yellow font-semibold" data-testid="auto-start-countdown">Início automático em {autoStartIn}s</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setAutoAdvance((v) => !v)}
              data-testid="auto-advance-toggle"
              className="w-full flex items-center justify-between bg-white/10 hover:bg-white/15 rounded-xl px-4 py-3 mb-4 transition-colors"
            >
              <span className="text-left text-sm">
                <span className="font-semibold block">Modo automático</span>
                <span className="text-white/70 text-xs">Inicia sozinho 15s após entrarem (reinicia a cada novo jogador) e avança as perguntas automaticamente. Desligue para controlar você mesmo.</span>
              </span>
              <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${autoAdvance ? "bg-brand-green" : "bg-white/30"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${autoAdvance ? "left-[22px]" : "left-0.5"}`} />
              </span>
            </button>
            <div className="mb-4" data-testid="bg-selector">
              <p className="text-sm font-semibold mb-2">Cenário de fundo</p>
              <div className="grid grid-cols-3 gap-2">
                {BGS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setBg(b.id)}
                    data-testid={`bg-option-${b.id}`}
                    className={`relative rounded-xl overflow-hidden h-16 ring-2 transition-all ${bg === b.id ? "ring-white scale-[1.03]" : "ring-transparent opacity-70 hover:opacity-100"}`}
                  >
                    <img src={`/${b.id}`} alt={b.label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 inset-x-0 bg-black/55 text-[10px] py-0.5 text-center">{b.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[200px] content-start">
              {players.length === 0 && <p className="text-white/60 text-sm">Aguardando jogadores entrarem...</p>}
              {players.map((p) => (
                <span key={p.pid} className="group inline-flex items-center gap-1.5 bg-white/20 rounded-full pl-3 pr-2 py-1.5 text-sm font-medium">
                  {p.avatar ? <span className="text-base leading-none">{p.avatar}</span> : null}{p.nickname}
                  <button onClick={() => send({ action: "kick", pid: p.pid })} className="opacity-60 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // COUNTDOWN — 3, 2, 1 antes do quiz começar
  if (phase === "countdown") {
    return (
      <div className="min-h-screen text-white flex flex-col items-center justify-center p-6" style={classroomBg} data-testid="countdown-screen">
        <GamePinBar pin={info.pin} />
        <p className="uppercase tracking-widest text-white/70 font-bold mb-6">Preparados?</p>
        <div
          key={countdown}
          className="font-heading font-black leading-none"
          style={{ fontSize: countdown === "GO" ? "9rem" : "14rem", animation: "countPop 1s ease-out both" }}
          data-testid="countdown-number"
        >
          {countdown === "GO" ? "JÁ!" : countdown}
        </div>
      </div>
    );
  }

  // REVELANDO — todos responderam antes do tempo (modo automático): 2, 1
  if (phase === "revealing") {
    return (
      <div className="min-h-screen text-white flex flex-col items-center justify-center p-6" style={classroomBg} data-testid="reveal-countdown-screen">
        <GamePinBar pin={info.pin} />
        <p className="uppercase tracking-widest text-brand-yellow font-bold mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-6 h-6" /> Todos responderam!
        </p>
        <p className="text-2xl sm:text-3xl font-heading font-bold mb-6">Revelando a resposta em…</p>
        <div
          key={revealCountdown}
          className="font-heading font-black leading-none"
          style={{ fontSize: "13rem", animation: "countPop 1s ease-out both" }}
          data-testid="reveal-countdown-number"
        >
          {revealCountdown}
        </div>
      </div>
    );
  }

  // ENDED — pódio final animado + confetes
  if (phase === "ended" && ended) {
    const board = ended.leaderboard || [];
    const top3 = board.slice(0, 3);
    const rest = board.slice(3, 5);
    const order = [1, 0, 2];
    const barH = { 0: 210, 1: 155, 2: 115 };
    const rise = { 0: 0.35, 1: 0, 2: 0.2 };
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col items-center p-6" data-testid="podium-final">
        <div className="w-full flex items-center justify-between"><Logo light /><span className="text-white/70">Fim de jogo</span></div>
        <div className="flex-1 flex flex-col justify-center items-center w-full max-w-3xl">
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-center flex items-center gap-3 mb-10"><Trophy className="w-10 h-10 text-brand-yellow" /> Pódio final</h1>
          {top3.length === 0 ? (
            <p className="text-white/60">Sem participantes.</p>
          ) : (
            <div className="flex items-end justify-center gap-4 sm:gap-6">
              {order.map((rank) => {
                const p = top3[rank];
                if (!p) return <div key={rank} className="w-24 sm:w-32" />;
                const style = podium[rank];
                const Icon = style.Icon;
                return (
                  <div key={p.pid} className="flex flex-col items-center w-24 sm:w-32">
                    <div className="flex flex-col items-center" style={{ animation: "podiumPop 0.5s ease-out both", animationDelay: `${rise[rank] + 0.55}s` }}>
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shrink-0 mb-1 ${style.badge}`}><Icon className="w-6 h-6 sm:w-7 sm:h-7" /></div>
                      <p className="font-bold text-sm sm:text-base truncate max-w-full px-1">{p.avatar ? `${p.avatar} ` : ""}{p.nickname}</p>
                      <p className="font-heading font-black text-lg tabular-nums">{p.score}</p>
                    </div>
                    <div className="relative w-full" style={{ height: barH[rank] }}>
                      <div className={`absolute inset-0 rounded-t-2xl ${style.wrap} shadow-xl`} style={{ transformOrigin: "bottom", animation: "podiumRise 0.7s cubic-bezier(0.22,1,0.36,1) both", animationDelay: `${rise[rank]}s` }} />
                      <span className="absolute top-3 left-0 right-0 text-center font-heading font-black text-3xl sm:text-4xl drop-shadow">{rank + 1}º</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {rest.length > 0 && (
            <div className="mt-6 space-y-2 max-w-md w-full">
              {rest.map((p, i) => (
                <div key={p.pid} className="flex items-center gap-4 rounded-xl px-4 py-2.5 bg-white/10" style={{ animation: "rankIn 0.5s ease-out both", animationDelay: `${1 + i * 0.12}s` }}>
                  <span className="font-heading font-black text-lg w-8 shrink-0">{i + 4}º</span>
                  <span className="font-bold flex-1 truncate">{p.avatar ? `${p.avatar} ` : ""}{p.nickname}</span>
                  <span className="font-heading font-black tabular-nums">{p.score}</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/resultados" className="mt-10">
            <Button className="rounded-full bg-white text-brand-blue hover:bg-slate-100 gap-2"><BarChart3 className="w-4 h-4" /> Ver relatório completo</Button>
          </Link>
        </div>
      </div>
    );
  }

  // REVEAL — quantidade de respostas por alternativa + resposta correta (5s)
  if (phase === "reveal" && reveal) {
    const maxCount = Math.max(...reveal.distribution, 1);
    return (
      <div className="min-h-screen text-white flex flex-col p-6" style={classroomBg}>
        <GamePinBar pin={info.pin} />
        <div className="flex items-center justify-between"><Logo light /><span className="text-white/80 bg-black/30 backdrop-blur px-3 py-1 rounded-full">{reveal.correct_count}/{reveal.total} acertaram</span></div>
        <div className="flex-1 max-w-4xl mx-auto w-full flex flex-col justify-center">
          <p className="text-center text-white/60 mb-3">Respostas assinaladas · a correta está destacada</p>
          <div className="grid grid-cols-2 gap-5 items-end h-72">
            {reveal.distribution.map((count, oi) => {
              const correct = reveal.correct_answers.includes(oi);
              return (
                <div key={oi} className="flex flex-col items-center justify-end h-full">
                  <div className="mb-1 flex items-center justify-center gap-2 h-14">
                    <span className="font-heading font-black text-2xl">{count}</span>
                    {correct && (
                      <span className="flex w-12 h-12 rounded-full bg-emerald-500 items-center justify-center shadow-lg ring-4 ring-emerald-300/40" style={{ animation: "podiumPop 0.45s ease-out both" }}>
                        <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.5} data-testid="reveal-correct-check" />
                      </span>
                    )}
                  </div>
                  <div className={`w-full ${colors[oi % 4]} rounded-t-xl transition-all ${correct ? "ring-4 ring-emerald-400" : "opacity-40"}`} style={{ height: `${(count / maxCount) * 100}%`, minHeight: "10px" }} />
                  <div className={`mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg ${correct ? "bg-emerald-500 text-white ring-2 ring-emerald-300" : `${colors[oi % 4]} opacity-40`} text-base w-full font-semibold`}>
                    {correct && <CheckCircle2 className="w-6 h-6" strokeWidth={2.5} />}{reveal.options[oi]}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-white/50 flex items-center justify-center gap-2" data-testid="reveal-hint"><Loader2 className="w-4 h-4 animate-spin" /> Mostrando o ranking em instantes…</p>
        </div>
      </div>
    );
  }

  // RANKING — lista Top 5 com os 3 primeiros em destaque (3s)
  if (phase === "ranking" && ranking) {
    const top5 = (ranking.leaderboard || []).slice(0, 5);
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-blue to-brand-purple text-white flex flex-col p-6">
        <GamePinBar pin={info.pin} />
        <div className="flex items-center justify-between"><Logo light /><span className="text-white/70">Placar parcial</span></div>
        <div className="flex-1 max-w-2xl mx-auto w-full flex flex-col justify-center">
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-center flex items-center justify-center gap-3 mb-8"><Trophy className="w-9 h-9 text-brand-yellow" /> Ranking · Top 5</h1>
          <div className="space-y-3" data-testid="ranking-list">
            {top5.length === 0 && <p className="text-center text-white/60">Sem pontuações ainda.</p>}
            {top5.map((p, i) => {
              const style = podium[i];
              const leader = i === 0;
              return (
                <div
                  key={p.pid}
                  data-testid={`rank-row-${i}`}
                  className={`flex items-center gap-4 rounded-2xl px-5 py-4 origin-left ${style ? style.wrap : "bg-white/10 text-white"}`}
                  style={{ animation: `rankIn 0.5s ease-out ${i * 0.12}s both${leader ? ", leaderGlow 1.8s ease-in-out 0.9s infinite" : ""}` }}
                >
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-black shrink-0 ${style ? style.badge : "bg-white/20 text-white"}`}>
                    {style ? <style.Icon className="w-6 h-6" /> : i + 1}
                  </div>
                  <span className="font-heading font-black text-2xl w-9 shrink-0">{i + 1}º</span>
                  <span
                    className="font-bold text-lg flex-1 truncate"
                    style={leader ? { animation: "leaderName 1.8s ease-in-out 0.9s infinite", display: "inline-block", transformOrigin: "left center" } : undefined}
                  >
                    {p.avatar ? `${p.avatar} ` : ""}{p.nickname}
                  </span>
                  <span className="font-heading font-black text-xl tabular-nums">{p.score}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            {ranking.is_last ? (
              <Button onClick={() => send({ action: "end" })} className="rounded-full bg-brand-yellow text-brand-ink hover:bg-amber-400 gap-2" data-testid="end-game-btn">Encerrar e ver pódio <Trophy className="w-4 h-4" /></Button>
            ) : (
              <Button onClick={() => send({ action: "next" })} className="rounded-full bg-white text-brand-blue hover:bg-slate-100 gap-2" data-testid="next-question-btn">Próxima pergunta <ChevronRight className="w-4 h-4" /></Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // QUESTION
  if (phase === "question" && question) {
    return (
      <div className="min-h-screen text-white flex flex-col p-6" style={classroomBg}>
        <GamePinBar pin={info.pin} />
        <div className="flex items-center justify-between">
          <Logo light />
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 bg-black/30 backdrop-blur px-4 py-2 rounded-full font-heading font-bold text-xl"><Clock className="w-5 h-5" /> {timeLeft}</span>
            <span className="flex items-center gap-1.5 bg-black/30 backdrop-blur px-4 py-2 rounded-full"><Users className="w-4 h-4" /> {answered.answered}/{answered.total}</span>
          </div>
        </div>
        <div className="mt-4 h-2.5 w-full max-w-5xl mx-auto rounded-full bg-black/30 overflow-hidden" data-testid="time-bar-track">
          <div
            className={`h-full rounded-full transition-[width] duration-200 ease-linear ${timeFrac < 0.25 ? "bg-red-500 animate-pulse" : timeFrac < 0.5 ? "bg-brand-yellow" : "bg-brand-green"}`}
            style={{ width: `${timeFrac * 100}%` }}
            data-testid="time-bar-fill"
          />
        </div>
        <div className="flex-1 max-w-5xl mx-auto w-full flex flex-col justify-center">
          <div className="bg-brand-ink/55 backdrop-blur-md rounded-3xl px-6 py-8 sm:px-10 sm:py-10 border border-white/10 shadow-2xl">
            <p className="text-center text-white/70 text-sm font-semibold uppercase tracking-widest">Questão {question.index + 1} de {question.total}</p>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black text-center leading-tight drop-shadow">{question.question}</h1>
            {question.image && (
              <div className="mt-6 flex justify-center">
                <img
                  src={quizImageUrl(question.image)}
                  alt="Figura da questão"
                  className="max-h-[38vh] rounded-2xl object-contain shadow-lg border border-white/10 bg-black/20"
                  data-testid="question-image"
                />
              </div>
            )}
            <div className="mt-10 grid grid-cols-2 gap-4">
              {question.options.map((opt, oi) => (
                <div key={oi} className={`flex items-center gap-3 p-5 rounded-2xl text-white font-bold text-lg sm:text-xl shadow-lg ${colors[oi % 4]}`}>{opt}</div>
              ))}
            </div>
          </div>
          <div className="mt-6 text-center">
            <Button onClick={() => send({ action: "reveal" })} className="rounded-full bg-white text-brand-ink hover:bg-slate-100 gap-2" data-testid="reveal-btn">Revelar agora</Button>
            <p className="mt-2 text-xs text-white/70">Ao acabar o tempo, as respostas são reveladas automaticamente.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ink flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-white" />
    </div>
  );
}
